import { memo } from 'react';
import { Play, Eye, Check } from 'lucide-react';
import type { Technique } from '../types';
import { TechniqueStatus } from '../types';
import { categories } from '../data/categories';
import { CATEGORY_ICONS } from '../data/icons';

const catLabel = new Map(categories.map((c) => [c.id, c.label]));

interface TechniqueRowProps {
  tech: Technique;
  status: TechniqueStatus;
  watched?: boolean;
  /** Small label shown after the category, e.g. "Up next". */
  badge?: string;
  onPlay: (tech: Technique) => void;
  /** Cycle status: not started → practicing → done → not started. */
  onCycle: (tech: Technique) => void;
  /** Show the right-side status cycle control. Default true. */
  showStatus?: boolean;
}

const STATUS_TITLE: Record<TechniqueStatus, string> = {
  [TechniqueStatus.Unchecked]: 'Mark complete',
  [TechniqueStatus.MarkedForPractice]: 'Mark complete',
  [TechniqueStatus.Completed]: 'Completed: tap to undo',
  [TechniqueStatus.Archived]: 'Archived',
};

function TechniqueRow({ tech, status, watched, badge, onPlay, onCycle, showStatus = true }: TechniqueRowProps) {
  return (
    <div className="tech-row">
      <button
        className={`tech-row-play${watched ? ' tech-row-play--watched' : ''}`}
        onClick={() => onPlay(tech)}
        disabled={!tech.instagramUrl}
        aria-label="Watch video"
      >
        {watched ? <Eye size={14} /> : <Play size={14} />}
      </button>

      <div className="tech-row-info">
        <span className="tech-row-name">{tech.name}</span>
        <span className="tech-row-sub">
          {(() => { const Icon = CATEGORY_ICONS[tech.categoryId]; return Icon ? <Icon size={12} style={{ marginRight: 4, verticalAlign: '-2px' }} /> : null; })()}
          {catLabel.get(tech.categoryId) ?? ''}
          {badge && <span className="tech-row-badge">{badge}</span>}
        </span>
      </div>

      {showStatus && (
        <button
          className={`tech-row-status tech-row-status--${status}`}
          onClick={() => onCycle(tech)}
          title={STATUS_TITLE[status]}
          aria-label={STATUS_TITLE[status]}
        >
          <Check size={15} />
        </button>
      )}
    </div>
  );
}

export default memo(TechniqueRow);
